import {
  PriceUtils,
  ServiceAction,
  ServiceProposalPartAction,
  hasDiscount,
} from "@repo/common"
import { gradeToWearLevel } from "@repo/common"
import { useTotalActionPrice } from "@repo/ui/hooks/use-total-action-price"
import { serviceActionMapper } from "@repo/ui/lib/service-action-mapper"
import { cn } from "@repo/ui/lib/utils"
import { Lock } from "lucide-react"
import * as React from "react"
import { useTranslations } from "use-intl"
import { OriginalPrice } from "../../components/original-price"
import { ImageDisplay } from "./image-display"
import { ProductOrderingIndicator } from "./product-ordering-indicator"
import { ProgressBar } from "./progress-bar"
import {
  SelectableCard,
  SelectableCardContent,
  SelectableCardDescription,
  SelectableCardHeader,
  SelectableCardIndicator,
  SelectableCardLabel,
  SelectableCardPrice,
  SelectableCardSection,
} from "./selectable-card"
import { ServiceProposalPartActionProductChoicesField } from "./service-proposal-part-action-product-choices-field"

interface ServiceProposalPartActionCardProps {
  data: ServiceProposalPartAction
  onProductSelect?: (productId: string, choiceId: string) => void
  selected: boolean
  onSelect: () => void
  interactive?: boolean
  selectable?: boolean
  className?: string
  withinPackage?: boolean
  productDecisions?: Record<string, string>
  showProductComments?: boolean
}

const ServiceProposalPartActionCard = React.forwardRef<
  HTMLDivElement,
  ServiceProposalPartActionCardProps
>(
  (
    {
      className,
      data,
      onProductSelect,
      selected,
      onSelect,
      interactive = true,
      selectable = true,
      withinPackage = false,
      productDecisions = {},
      showProductComments = true,
    },
    ref,
  ) => {
    const t = useTranslations()
    const wearLevel =
      data.partGrade === null ? null : gradeToWearLevel(data.partGrade)
    const actionInfo = React.useMemo(
      () => serviceActionMapper(data.action),
      [data.action],
    )
    const actionLabel = data.actionLabel ?? t(actionInfo.labelKey)

    const getColorFromWearLevel = (wearLevel: number) => {
      if (wearLevel >= 75) return "success"
      if (wearLevel >= 50) return "primary"
      if (wearLevel >= 25) return "warning"
      return "destructive"
    }

    const cardColor =
      wearLevel === null ? "secondary" : getColorFromWearLevel(wearLevel)

    const shouldDimContent = !selected // All unselected cards get dimmed content

    const getTextColorClass = (color: string) => {
      switch (color) {
        case "success":
          return "text-success-foreground"
        case "primary":
          return "text-primary-foreground"
        case "warning":
          return "text-warning-foreground"
        case "destructive":
          return "text-destructive-foreground"
        default:
          return ""
      }
    }

    const { total: totalPrice, rawTotal: rawTotalPrice } = useTotalActionPrice({
      ...data,
      productDecisions,
    })

    // A mandatory action, like a reception-approved one, is locked: the
    // customer cannot decline it. Reception approval takes precedence for the
    // shown reason.
    const isReceptionLocked = data.isApprovedViaReception
    const isLocked = isReceptionLocked || data.isMandatory

    return (
      <SelectableCard
        ref={ref}
        className={cn(
          className,
          isLocked && "border-secondary-foreground bg-secondary-background",
        )}
        selected={selected}
        onSelect={onSelect}
        interactive={!isLocked && selectable && interactive}
        color={cardColor}
        aria-disabled={isLocked || undefined}
      >
        <SelectableCardHeader>
          <div className="flex gap-2 items-center">
            <ImageDisplay image={data.partImage} alt={data.partName} />
            <div className="space-y-1">
              <SelectableCardLabel>{actionLabel}</SelectableCardLabel>
              <SelectableCardDescription>
                {data.partName}
              </SelectableCardDescription>
            </div>
          </div>

          {isLocked ? (
            <div className="ml-auto flex flex-shrink-0 items-center gap-1 self-start text-foreground">
              <Lock className="size-4" />
              {isReceptionLocked ? (
                <span className="sr-only">
                  {t("ui.proposalForm.approvedAtReception")}
                </span>
              ) : (
                <span className="text-xs font-medium">
                  {t("ui.common.required")}
                </span>
              )}
            </div>
          ) : (
            selectable && <SelectableCardIndicator />
          )}
        </SelectableCardHeader>

        <SelectableCardContent>
          {!withinPackage && (
            <SelectableCardPrice
              className={shouldDimContent ? "opacity-40" : ""}
            >
              {rawTotalPrice > totalPrice ? (
                <span className="flex items-center gap-2">
                  <OriginalPrice>
                    {PriceUtils.format(rawTotalPrice)}
                  </OriginalPrice>
                  <span className="text-destructive-foreground">
                    {PriceUtils.format(totalPrice)}
                  </span>
                  {hasDiscount(data.laborPriceDiscount) ? (
                    <span className="text-destructive-foreground text-sm">
                      {t("ui.receipt.discountOnLabor", {
                        discount: PriceUtils.formatDiscountLabel(
                          data.laborPriceDiscount,
                        ),
                      })}
                    </span>
                  ) : null}
                </span>
              ) : (
                PriceUtils.format(totalPrice)
              )}
            </SelectableCardPrice>
          )}

          {data.action !== ServiceAction.MOUNT && wearLevel !== null && (
            <SelectableCardSection
              className={shouldDimContent ? "opacity-40" : ""}
            >
              <SelectableCardLabel
                className={shouldDimContent ? getTextColorClass(cardColor) : ""}
              >
                {t("ui.proposalForm.remainingLifetime")}
              </SelectableCardLabel>
              <ProgressBar
                value={wearLevel}
                showPercentage={true}
                color={isLocked ? "secondary" : undefined}
              />
            </SelectableCardSection>
          )}

          {data.comment && (
            <SelectableCardSection
              className={cn("space-y-0.5", {
                "opacity-40": shouldDimContent,
              })}
            >
              <SelectableCardLabel>
                {t("ui.juloActionInput.comment")}
              </SelectableCardLabel>
              <SelectableCardDescription>
                {data.comment}
              </SelectableCardDescription>
            </SelectableCardSection>
          )}

          {data.products.length > 0 && (
            <SelectableCardSection
              className={shouldDimContent ? "opacity-40" : ""}
            >
              <SelectableCardLabel className="-mb-1">
                {t("ui.proposalForm.includedProducts")}
                <span className="float-right">{t("ui.proposalForm.cost")}</span>
              </SelectableCardLabel>
              <div className="space-y-1">
                {data.products.map((product) => {
                  const quantity = product.quantity ?? 1
                  const activeDiscount = hasDiscount(product.discount)
                    ? product.discount
                    : null
                  // The "Kostnad" column shows the line total (unit price ×
                  // quantity); without this a 2-pack at 149 kr/st reads as
                  // "149 kr" yet the headline total charges 298 kr.
                  const originalTotal = product.price * quantity
                  const discountedTotal = activeDiscount
                    ? PriceUtils.applyDiscount(
                        originalTotal,
                        product.discount ?? null,
                      )
                    : originalTotal
                  return (
                    <div key={product.id}>
                      <div className="flex justify-between items-center gap-2">
                        <SelectableCardDescription>
                          {product.title}
                          {quantity > 1 && (
                            <span className="text-muted-foreground">
                              {" "}
                              (
                              {t("ui.receipt.quantityAtPrice", {
                                quantity,
                                price: PriceUtils.format(product.price),
                              })}
                              )
                            </span>
                          )}
                        </SelectableCardDescription>
                        {activeDiscount ? (
                          <div className="flex items-center gap-2">
                            <OriginalPrice>
                              {PriceUtils.format(originalTotal)}
                            </OriginalPrice>
                            <SelectableCardDescription className="text-destructive-foreground">
                              {PriceUtils.format(discountedTotal)}
                            </SelectableCardDescription>
                            <span className="text-xs text-destructive-foreground">
                              {PriceUtils.formatDiscountLabel(activeDiscount)}
                            </span>
                          </div>
                        ) : (
                          <SelectableCardDescription>
                            {PriceUtils.format(originalTotal)}
                          </SelectableCardDescription>
                        )}
                      </div>
                      {product.needsOrdering && (
                        <ProductOrderingIndicator
                          expectedDeliveryDate={product.expectedDeliveryDate}
                          className="mt-1"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </SelectableCardSection>
          )}

          <ServiceProposalPartActionProductChoicesField
            interactive={interactive}
            productChoices={data.productChoices}
            onProductSelect={onProductSelect}
            shouldDimContent={shouldDimContent}
            productDecisions={productDecisions}
            showComments={showProductComments}
          />
        </SelectableCardContent>
      </SelectableCard>
    )
  },
)

ServiceProposalPartActionCard.displayName = "ServiceProposalPartActionCard"

export {
  ServiceProposalPartActionCard,
  type ServiceProposalPartActionCardProps,
}
